"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Simplify } from "@/prismicio-types";
import { Content } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import Image from "next/image";
import React from "react";
import employabilityAndCareerServiceSectionLeftShape from "@/assets/employabilityAndCareerServiceSectionLeftShape.svg";
import noise from "@/assets/noise.png";
import { motion } from "framer-motion";

type Props = {
  data: Simplify<Content.CallToActionCardSliceFeesAndFundingPrimary>;
};

export default function FeesFundingCTA({ data }: Props) {
  return (
    <Card className="shadow-none border-none bg-[#033D61] text-white relative rounded-xl h-[650px] md:h-[280px] lg:h-[250px]">
      <CardContent className="flex flex-col md:flex-row py-6">
        {/* texts */}

        <div className="space-y-8 md:space-y-4 lg:space-y-6 md:w-1/2 lg:w-3/4 xl:w-4/5">
          {/* heading */}

          <div className="flex gap-4">
            <Button
              size="icon"
              className="bg-[#0798F2]/50 rounded-full shrink-0"
            >
              <PrismicNextImage field={data.icon} alt="" />
            </Button>

            <motion.div
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              viewport={{ once: true }}
            >
              <PrismicRichText
                field={data.heading}
                components={{
                  heading6: ({ children }) => (
                    <h6 className="text-2xl md:text3xl font-semibold">
                      {children}
                    </h6>
                  ),
                }}
              />
            </motion.div>
          </div>

          {/* content */}

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.4, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <PrismicRichText
              field={data.sub_heading}
              components={{
                paragraph: ({ children }) => (
                  <p className="text-xl font-merriweather xl:mb-14">
                    {children}
                  </p>
                ),
              }}
            />
          </motion.div>

          <motion.div
            className="flex gap-2"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.8, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <Button
              asChild
              size="lg"
              className="rounded-full py-3 px-5 bg-linear-to-r from-[#43D9BD] to-[#0798F2] text-primary w-56"
            >
              <PrismicNextLink field={data.cta_button_primary} />
            </Button>

            <Button
              asChild
              size="lg"
              className="bg-transparent border rounded-full py-3 px-5 w-56"
            >
              <PrismicNextLink field={data.cta_button_secondary} />
            </Button>
          </motion.div>
        </div>

        {/* image */}

        <PrismicNextImage
          field={data.promo_image}
          alt=""
          className="absolute bottom-0 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-2 h-80 xl:h-[450px] w-auto pointer-events-none z-10"
        />
      </CardContent>

      {/* overlays */}

      <Image
        src={employabilityAndCareerServiceSectionLeftShape.src}
        alt="left shape"
        width={521}
        height={482}
        className="absolute -left-20 -top-20 h-96 w-auto md:h-[480px] select-none pointer-events-none"
      />
      <div
        className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4] pointer-events-none"
        style={{
          background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
        }}
      />
    </Card>
  );
}
